import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  order_id: "", country: "India", city: "", state: "", zip_code: "", payment_method: "",
  rvp_order_id: "", rvp_tracking_number: "", rvp_courier_partner: "", rvp_reason: "", reverse_pickup_date: "",
};

function toDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); } catch { return ""; }
}

export default function ReversePickupDialog({ open, onOpenChange, existing, onDone }) {
  const isFlag = !!existing;
  const [couriers, setCouriers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/couriers").then((r) => setCouriers(r.data)).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        ...EMPTY,
        rvp_order_id: existing.rvp_order_id || "",
        rvp_tracking_number: existing.rvp_tracking_number || "",
        rvp_courier_partner: existing.rvp_courier_partner || "",
        rvp_reason: existing.rvp_reason || "",
        reverse_pickup_date: toDateInput(existing.reverse_pickup_date),
      });
    } else {
      setForm(EMPTY);
    }
  }, [existing, open]);

  // Auto-fill state (locked) + city from the PIN code (standalone entries)
  useEffect(() => {
    if (isFlag) return;
    const pin = (form.zip_code || "").replace(/\D/g, "");
    if (pin.length < 5) return;
    let active = true;
    const t = setTimeout(() => {
      api.get(`/pincode/${pin}`, { params: { country: form.country || "India" } }).then((r) => {
        if (!active) return;
        setForm((f) => ({ ...f, state: r.data.state || "", city: r.data.city || "" }));
      }).catch(() => {});
    }, 400);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zip_code, form.country, isFlag]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (pending) => {
    const rvp = {
      rvp_order_id: form.rvp_order_id,
      rvp_tracking_number: form.rvp_tracking_number,
      rvp_courier_partner: form.rvp_courier_partner,
      rvp_reason: form.rvp_reason,
      reverse_pickup_date: form.reverse_pickup_date ? new Date(form.reverse_pickup_date).toISOString() : null,
      rvp_pending: !!pending,
    };
    setSaving(true);
    try {
      if (isFlag) {
        await api.post(`/shipments/${existing.id}/reverse-pickup`, rvp);
        toast.success(pending ? "Reverse pickup requested — awaiting details" : "Reverse pickup details saved");
      } else {
        if (!form.order_id) { toast.error("Original order ID is required"); setSaving(false); return; }
        if (!form.country) { toast.error("Please select a country"); setSaving(false); return; }
        if (!form.payment_method) { toast.error("Please select a payment method (Prepaid or COD)"); setSaving(false); return; }
        await api.post("/shipments", {
          order_id: form.order_id, country: form.country, city: form.city, state: form.state, zip_code: form.zip_code,
          payment_method: form.payment_method, is_reverse_pickup: true, ...rvp,
        });
        toast.success(pending ? "Reverse pickup added — awaiting details" : "Reverse pickup entry added");
      }
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="rvp-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isFlag ? `Reverse pickup — #${existing.order_id}` : "Add reverse pickup entry"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Enter the reverse pickup details, or hit <span className="font-medium">Skip for now</span> and add them later
          once the dispatch team shares the reverse pickup ID, AWB and courier.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {!isFlag && (
            <div>
              <Label>Original Order ID *</Label>
              <Input data-testid="rvp-order-id" value={form.order_id} onChange={set("order_id")} className="mt-1.5" placeholder="Original order ID" />
            </div>
          )}
          <div>
            <Label>Reverse pickup ID</Label>
            <Input data-testid="rvp-rvp-order-id" value={form.rvp_order_id} onChange={set("rvp_order_id")} className="mt-1.5" placeholder="RVP ID" />
          </div>
          <div>
            <Label>Reverse pickup AWB / tracking</Label>
            <Input data-testid="rvp-rvp-tracking" value={form.rvp_tracking_number} onChange={set("rvp_tracking_number")} className="mt-1.5" placeholder="RVP AWB" />
          </div>
          <div>
            <Label>Reverse pickup courier partner</Label>
            <Select value={form.rvp_courier_partner || ""} onValueChange={(v) => setForm((f) => ({ ...f, rvp_courier_partner: v }))}>
              <SelectTrigger data-testid="rvp-courier" className="mt-1.5"><SelectValue placeholder="Select courier" /></SelectTrigger>
              <SelectContent>
                {couriers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reverse pickup created date</Label>
            <Input type="date" data-testid="rvp-date" value={form.reverse_pickup_date} onChange={set("reverse_pickup_date")} className="mt-1.5" />
          </div>
          {!isFlag && (
            <>
              <div>
                <Label>Country *</Label>
                <Select value={form.country || ""} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger data-testid="rvp-country" className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment method *</Label>
                <Select value={form.payment_method || ""} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                  <SelectTrigger data-testid="rvp-payment" className="mt-1.5"><SelectValue placeholder="Prepaid or COD" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="cod">COD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zip Code</Label>
                <Input data-testid="rvp-zip" value={form.zip_code} onChange={set("zip_code")} className="mt-1.5" placeholder="Enter PIN to auto-fill city & state" />
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="rvp-city" value={form.city} readOnly disabled
                       className="mt-1.5 bg-[#F4F6F4] text-muted-foreground cursor-not-allowed" placeholder="Auto-filled from PIN" />
              </div>
              <div>
                <Label>State</Label>
                <Input data-testid="rvp-state" value={form.state} readOnly disabled
                       className="mt-1.5 bg-[#F4F6F4] text-muted-foreground cursor-not-allowed" placeholder="Auto-filled from PIN" />
                <p className="text-[11px] text-muted-foreground mt-1">City &amp; State auto-fill from the PIN code</p>
              </div>
            </>
          )}
          <div className="sm:col-span-2">
            <Label>Reason for reverse pickup</Label>
            <Textarea data-testid="rvp-reason" value={form.rvp_reason} onChange={set("rvp_reason")} rows={2} className="mt-1.5"
                      placeholder="Why is this a reverse pickup? (e.g. wrong item, customer return, damaged)" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rvp-cancel">Cancel</Button>
          <Button variant="outline" onClick={() => submit(true)} disabled={saving} data-testid="rvp-skip"
                  className="border-[#C5A253] text-[#8A6D1F] hover:bg-[#FBEFD9]">
            Skip for now
          </Button>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="rvp-save" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isFlag ? "Save details" : "Add entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
